"use client"

import Backdrop from "@components/Backdrop"
import { Chevron, Close } from "@components/icons"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import Logo, { UAGLogoWrapper } from "@components/Logo"
import MenuItem, { ButtonContainer, LinkContainer } from "@components/MenuItem"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  body16MainStyles,
  h5Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { getIn } from "@utils/index"
import { MobileAndTabletOnly } from "@utils/MediaQuery"
import { FilterFunnelProps, MenuDataProps } from "@utils/types"
import { getShopURLForDevice } from "@utils/urls"
import { Link, navigate } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"

import { Headers } from "./common"
import MenuSubGroup from "./MenuSubGroup"
import SelectBrand from "./SelectBrand"
import SelectDevice from "./SelectDevice"

const openProps = `
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`
const Container = styled.div<{ $isOpen?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  opacity: 0;
  transform: translateX(100%);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  overflow: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  cursor: default;
  ${({ $isOpen }) => ($isOpen ? openProps : "")}

  ${UAGLogoWrapper} {
    width: 75px;
  }

  @media ${breakpoints.tablet} {
    max-width: 64%;
  }
`

const CloseButton = styled.button`
  flex-direction: row;
  align-items: center;
  display: flex;
  transform: translateX(16px);
  width: 48px;
  height: 48px;
  padding: 0;
  outline: none;
  border: none;
  background: none;

  &:focus,
  &:active {
    outline: none;
  }

  @media ${breakpoints.laptop} {
    transform: translateX(-6px);
    transition: background-color ${animation.quick} ${cubicBezier};
    width: 44px;
    height: 44px;
    border-radius: 100px;
    background-color: ${colors.dividerLineGray};

    svg {
      transition: stroke ${animation.quick} ${cubicBezier};
      stroke: ${colors.mediumDarkGray};
    }

    &:hover {
      background-color: ${colors.lightGray};
      cursor: pointer;

      svg {
        stroke: ${colors.offBlack};
      }
    }
  }
`

const Banners = styled.div`
  padding: 24px 20px 4px;
`

const Banner = styled.div`
  display: flex;
  width: 100%;
  height: 88px;
  margin-bottom: 12px;
  border-radius: 2px;
  background-color: ${colors.dividerLineGray};
`

const BannerLink = styled(Link)`
  display: flex;
  width: 100%;
  height: 88px;
  margin-bottom: 12px;
  border-radius: 2px;
  background-color: ${colors.dividerLineGray};
`

const BannerText = styled.div`
  ${h5Styles}
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-start;
  display: flex;
  width: 50%;
  padding-top: 4px;
  padding-left: 12px;
  color: ${colors.offBlack};
`

const BannerImageWrapper = styled.div`
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 50%;
  overflow: hidden;
`

export const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  transform: translateY(-4px);
`

const DeviceTypes = styled.div`
  overflow: hidden;

  ${ButtonContainer} {
    color: ${colors.offBlack};

    svg {
      stroke: ${colors.offBlack};
    }
  }

  ${LinkContainer} {
    color: ${colors.offBlack};

    svg {
      stroke: ${colors.offBlack};
    }
  }
`

const RewardsRow = styled.div`
  ${body16MainStyles}
  display: flex;
  justify-content: space-between;
  padding: 14px 10px 14px 20px;
  color: ${colors.mediumGray};
`
const linkStyles = css`
  ${body14Styles}
  margin-left: 25px;
`

const RewardsLink = styled(Link)`
  ${linkStyles}
  font: ${smallcapsBold13Styles};
  color: ${colors.white};
  background: #f26722;
  padding: 3px 7px 3px 7px;
  border-radius: 2px;
`

const MenuItems = styled.div`
  ${ButtonContainer} {
    color: ${colors.mediumGray};
  }

  ${LinkContainer} {
    color: ${colors.mediumGray};
  }
`

const Footer = styled.div`
  flex-wrap: wrap;
  display: flex;
  padding: 24px 20px;
`

const footerLinkBaseProps = `
  ${body14Styles}
    flex-shrink: 0;
    display: flex;
    width: 50%;
    height: auto;
    min-height: 52px;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-start;
    color: ${colors.mediumGray};
    border: none;
    cursor: pointer;

    &:active {
      outline: none;
    }
    &:focus {
      outline: none;
    }
`

const FooterLinkInternal = styled(Link)`
  ${footerLinkBaseProps}
`

const FooterLinkExternal = styled.a`
  ${footerLinkBaseProps}
`

const isBrowser = () => typeof window !== "undefined"

export interface MenuLinkProps {
  external?: boolean
  items?: Array<{ external?: boolean; label: string; link: string }>
  label: string
  link?: string
}

interface MobileMenuProps {
  config?: {
    brand?: string
    deviceType?: string
    showShopOnly?: boolean
  }
  filterFunnel: FilterFunnelProps
  globalData: any
  isLoggedIn?: boolean
  isOpen: boolean
  menuData: MenuDataProps
  onLogout?: () => {}
  onRequestClose?: () => void
  rewardsPoints?: number
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  config,
  filterFunnel,
  globalData,
  isLoggedIn,
  isOpen = false,
  menuData,
  onLogout,
  onRequestClose,
  rewardsPoints,
}) => {
  const menuRef: any = React.useRef(null)
  const selectedInitialState = { brand: undefined, deviceType: undefined }
  const [selected, setSelected] = React.useState<{
    brand: string | undefined
    deviceType: string | undefined
  }>(selectedInitialState)

  const clearAll = () => {
    setSelected(selectedInitialState)
  }

  const selectDeviceType = (deviceTypeSlug: string) => {
    setSelected({
      brand: undefined,
      deviceType: deviceTypeSlug,
    })
  }

  const clearDeviceType = () => {
    setSelected(selectedInitialState)
  }

  const selectBrand = (brandSlug: string) => {
    setSelected({
      brand: brandSlug,
      deviceType: selected.deviceType,
    })
  }

  const clearBrand = () => {
    setSelected({
      brand: undefined,
      deviceType: selected.deviceType,
    })
  }

  const [menuSubGroup, setSubGroup] = React.useState<string | undefined>(
    undefined
  )

  const clearSubGroup = () => {
    setSubGroup(undefined)
  }

  React.useEffect(() => {
    if (!isOpen) {
      clearAll()
      clearSubGroup()
    } else if (config && config.deviceType) {
      setSelected({
        brand: config.brand,
        deviceType: config.deviceType,
      })
    }
  }, [isOpen])

  useModalBackgroundClose({
    excludeIds: ["mobileCollectionsFilterButton"],
    isOpen: !!(isOpen && onRequestClose),
    modalRef: menuRef,
    onRequestClose: onRequestClose || (() => {}),
  })

  const selectedDeviceTypeMegaMenuLinks =
    globalData?.megaMenuLinks?.filter(
      ({ slug }) => slug === selected.deviceType
    )[0]?.megaMenuLinks || []

  const formatDeviceType = ({ menuBannerImage, menuLabel, slug }) => {
    const thisFilterFunnel: any = getIn(filterFunnel, `deviceType.${slug}`, {})
    const brands = Object.keys(thisFilterFunnel)
    const brand = brands.length > 0 && thisFilterFunnel[brands[0]]

    if (slug === "urban-armor-gear-power-and-cables") {
      return {
        banner: menuBannerImage?.file?.url || null,
        label: menuLabel,
        link: "/collections/urban-armor-gear-power-and-cables",
        onClick: onRequestClose,
      }
    } else if (menuLabel === "Gear") {
      return {
        banner: menuBannerImage?.file?.url || null,
        label: menuLabel,
        link: "/collections/gears",
        onClick: onRequestClose,
      }
    } else if (slug === "special-ops") {
      return {
        banner: menuBannerImage?.file?.url || null,
        label: menuLabel,
        link: "/collections/special-ops",
        onClick: onRequestClose,
      }
    } else if (brand && brand.devices.length === 1) {
      const shopURL = getShopURLForDevice(brand.devices[0].slug)
      if (isBrowser() && getIn(window, "location.pathname") !== shopURL) {
        return {
          banner: menuBannerImage?.file?.url || null,
          label: menuLabel,
          link: shopURL,
        }
      } else {
        return {
          banner: menuBannerImage?.file?.url || null,
          label: menuLabel,
          onClick: onRequestClose,
        }
      }
    } else if (brand) {
      return {
        banner: menuBannerImage?.file?.url || null,
        label: menuLabel,
        onClick: () => {
          setSelected({
            brand: brand.brandSlug,
            deviceType: slug,
          })
        },
      }
    } else {
      return {
        banner: menuBannerImage?.file?.url || null,
        label: menuLabel,
        onClick: () => {
          selectDeviceType(slug)
        },
      }
    }
  }

  const formatMenuLink = ({ external, items, label, link }) => {
    if (items) {
      return {
        label,
        onClick: () => {
          setSubGroup(label)
        },
      }
    }

    return {
      external,
      label,
      link,
    }
  }

  const menuItems = getIn(globalData, "menuItems", [])
  const menuItemBanners = getIn(globalData, "menuItemBanners", []).filter(
    ({ menuBannerImage }) => !!menuBannerImage?.file?.url
  )
  const menuItemBannerSlugs = menuItemBanners.map((banner) => banner.slug)
  const specialOpsBannerImage = getIn(
    globalData,
    "originalArtBannerImage",
    null
  )
  const combinedMenuItemBanners = [
    ...menuItemBanners,
    {
      menuBannerImage: specialOpsBannerImage,
      menuLabel: "Original Art",
      slug: "special-ops",
    },
  ]
  const filteredMenuItems = menuItems.filter(
    ({ slug }) => !menuItemBannerSlugs.includes(slug)
  )

  const formattedBanners = React.useMemo(() => {
    return combinedMenuItemBanners.map(formatDeviceType)
  }, [config, filterFunnel, globalData, combinedMenuItemBanners])

  const formattedDeviceTypes = React.useMemo(
    () => filteredMenuItems.map(formatDeviceType),
    [config, filterFunnel, globalData, filteredMenuItems]
  )

  const menuLinks = getIn(globalData, "menuLinks", [])
  const formattedMenuLinks = React.useMemo(
    () => menuLinks.map(formatMenuLink),
    [config, filterFunnel, globalData]
  )
  const exploreLink = formattedMenuLinks[0]
  const footerLinks = formattedMenuLinks.slice(1)

  const handleRewardsOnClick = () => {
    navigate("/rewards")
  }

  return (
    <MobileAndTabletOnly>
      <Backdrop $isOpen={isOpen}>
        <Container $isOpen={isOpen} id="mobileMenu" ref={menuRef}>
          <Headers>
            <Logo />
            <CloseButton onClick={onRequestClose}>
              <Close strokeWidth="1.5px" />
            </CloseButton>
          </Headers>
          {isLoggedIn && rewardsPoints && (
            <RewardsRow onClick={handleRewardsOnClick}>
              Rewards
              <RewardsLink to="/rewards">{rewardsPoints} Points</RewardsLink>
            </RewardsRow>
          )}
          <Banners>
            {formattedBanners.map((props) =>
              props.link ? (
                <BannerLink
                  key={props.label}
                  onClick={props.onClick}
                  to={props.link}
                >
                  <BannerText>
                    {props.label}
                    <IconWrapper>
                      <Chevron strokeColor={colors.black} />
                    </IconWrapper>
                  </BannerText>
                  <BannerImageWrapper>
                    <ImageHelperContainer>
                      <ImageHelper alt={props.label} src={props.banner || ""} />
                    </ImageHelperContainer>
                  </BannerImageWrapper>
                </BannerLink>
              ) : (
                <Banner key={props.label} onClick={props.onClick}>
                  <BannerText>
                    {props.label}
                    <IconWrapper>
                      <Chevron strokeColor={colors.black} />
                    </IconWrapper>
                  </BannerText>
                  <BannerImageWrapper>
                    <ImageHelper alt={props.label} src={props.banner || ""} />
                  </BannerImageWrapper>
                </Banner>
              )
            )}
          </Banners>
          <DeviceTypes>
            {formattedDeviceTypes.map((props) => (
              <MenuItem key={props.label} {...props} />
            ))}
          </DeviceTypes>
          {(!config || !config.showShopOnly) && (
            <>
              <MenuItems>
                <MenuItem {...exploreLink} />
                {isLoggedIn && (
                  <MenuItem hideIcon label="Logout" onClick={onLogout} />
                )}
                <MenuSubGroup
                  menuSubGroup={menuSubGroup}
                  menuSubGroups={menuLinks.filter(({ items }) => !!items)}
                  onRequestClose={clearSubGroup}
                />
              </MenuItems>
              <Footer>
                {footerLinks.map((link) =>
                  link.external ? (
                    <FooterLinkExternal
                      href={link.link}
                      key={link.label}
                      rel="noopener"
                      target="_blank"
                    >
                      {link.label}
                    </FooterLinkExternal>
                  ) : (
                    <FooterLinkInternal
                      key={link.label}
                      onClick={link.onClick}
                      to={link.link}
                    >
                      {link.label}
                    </FooterLinkInternal>
                  )
                )}
              </Footer>
            </>
          )}
          <SelectBrand
            data={menuData}
            deviceTypeSlug={selected.deviceType}
            filterFunnel={filterFunnel}
            megaMenuLinks={selectedDeviceTypeMegaMenuLinks}
            onRequestClose={clearDeviceType}
            onSelectBrand={selectBrand}
          />
          <SelectDevice
            brandSlug={selected.brand}
            data={menuData}
            deviceTypeSlug={selected.deviceType}
            onRequestClose={clearBrand}
          />
        </Container>
      </Backdrop>
    </MobileAndTabletOnly>
  )
}

export default MobileMenu
